import React, { createContext, useContext, useState, useEffect } from 'react';
import { Language } from '../types';

interface Translations {
  [key: string]: {
    fi: string;
    en: string;
    sv: string;
  };
}

export const translations: Translations = {
  // Brand & Header
  brandTitle: {
    fi: 'The Oven Story',
    en: 'The Oven Story',
    sv: 'The Oven Story'
  },
  tagline: {
    fi: 'Aito puu-uunipizzeria & pohjoismainen keittiö',
    en: 'Artisan Wood-Fired Pizzeria & Nordic Kitchen',
    sv: 'Vedugnspizzeria & Nordiskt kök'
  },
  table: {
    fi: 'Pöytä',
    en: 'Table',
    sv: 'Bord'
  },
  staffPortal: {
    fi: 'Henkilökunta / Keittiö',
    en: 'Staff / Kitchen',
    sv: 'Personal / Kök'
  },
  customerMode: {
    fi: 'Asiakasvalikko',
    en: 'Customer Menu',
    sv: 'Kundmeny'
  },

  // Customer Welcome Modal
  welcomeTitle: {
    fi: 'Tervetuloa The Oven Storyyn!',
    en: 'Welcome to The Oven Story!',
    sv: 'Välkommen till The Oven Story!'
  },
  welcomeSub: {
    fi: 'Skannaa tai valitse pöytäsi ja aloita tilaaminen vaivattomasti suoraan puhelimellasi.',
    en: 'Scan or select your table and start ordering effortlessly from your phone.',
    sv: 'Skanna eller välj ditt bord och beställ enkelt direkt från telefonen.'
  },
  yourName: {
    fi: 'Nimesi',
    en: 'Your Name',
    sv: 'Ditt namn'
  },
  namePlaceholder: {
    fi: 'Esim. Matti Meikäläinen',
    en: 'E.g. John Doe',
    sv: 'T.ex. Johan Svensson'
  },
  phoneLabel: {
    fi: 'WhatsApp / Puhelinnumero',
    en: 'WhatsApp / Phone Number',
    sv: 'WhatsApp / Telefonnummer'
  },
  tableNumber: {
    fi: 'Pöydän numero',
    en: 'Table Number',
    sv: 'Bordsnummer'
  },
  gdprConsent: {
    fi: 'Hyväksyn, että The Oven Story tallentaa nimeni ja puhelinnumeroni tilauksen tilapäistä WhatsApp-tilapäivitystä varten (EU GDPR).',
    en: 'I agree that The Oven Story may store my name and phone for temporary WhatsApp order status updates (EU GDPR).',
    sv: 'Jag godkänner att The Oven Story sparar mitt namn och telefonnummer för tillfälliga orderuppdateringar via WhatsApp (EU GDPR).'
  },
  startBrowsing: {
    fi: 'Avaa Menu & Aloita Tilaus',
    en: 'Open Menu & Start Order',
    sv: 'Öppna meny & Beställ'
  },

  // Menu Explorer
  searchPlaceholder: {
    fi: 'Etsi pizzoja, pastoja, juomia...',
    en: 'Search pizzas, pastas, drinks...',
    sv: 'Sök pizzor, pasta, drycker...'
  },
  allCategories: {
    fi: 'Kaikki',
    en: 'All',
    sv: 'Alla'
  },
  popularTag: {
    fi: 'Suosittu 🔥',
    en: 'Popular 🔥',
    sv: 'Populär 🔥'
  },
  chefTag: {
    fi: 'Suosittelemme ⭐',
    en: 'Chef Pick ⭐',
    sv: 'Rekommenderas ⭐'
  },
  addToCart: {
    fi: 'Lisää',
    en: 'Add',
    sv: 'Lägg till'
  },
  soldOut: {
    fi: 'Loppuunmyyty',
    en: 'Sold Out',
    sv: 'Slutsåld'
  },

  // Dietary
  dietaryAll: { fi: 'Kaikki', en: 'All', sv: 'Alla' },
  dietaryG: { fi: 'Gluteeniton (G)', en: 'Gluten-Free (G)', sv: 'Glutenfri (G)' },
  dietaryL: { fi: 'Laktoositon (L)', en: 'Lactose-Free (L)', sv: 'Laktosfri (L)' },
  dietaryVEG: { fi: 'Vegaani (VEG)', en: 'Vegan (VEG)', sv: 'Vegansk (VEG)' },
  dietaryVL: { fi: 'Vähälaktoosinen (VL)', en: 'Low Lactose (VL)', sv: 'Låglaktos (VL)' },

  // Dish Detail Modal
  customiseDish: { fi: 'Muokkaa Annosta', en: 'Customize Dish', sv: 'Anpassa rätten' },
  selectCrust: { fi: 'Valitse pohja', en: 'Choose crust', sv: 'Välj botten' },
  extraToppings: { fi: 'Lisätäytteet', en: 'Extra Toppings', sv: 'Extra pålägg' },
  specialNotesLabel: { fi: 'Erityistoiveet keittiölle / Allergiat', en: 'Kitchen Notes / Allergies', sv: 'Önskemål till köket / Allergier' },
  specialNotesPlaceholder: { fi: 'Esim. Ilman sipulia, kastike erikseen...', en: 'E.g. No onion, sauce on side...', sv: 'T.ex. Utan lök, sås vid sidan...' },
  quantity: { fi: 'Määrä', en: 'Quantity', sv: 'Antal' },
  addToOrder: { fi: 'Lisää Tilaukseen', en: 'Add to Order', sv: 'Lägg till i beställning' },

  // Cart
  cartTitle: { fi: 'Tilauksesi', en: 'Your Order', sv: 'Din beställning' },
  cartEmpty: { fi: 'Ostoskorisi on vielä tyhjä. Valitse herkullisia annoksia menusta!', en: 'Your cart is empty. Add delicious items from the menu!', sv: 'Din varukorg är tom. Välj läckra rätter från menyn!' },
  subtotalNet: { fi: 'Veroton hinta (ALV 0%)', en: 'Net Subtotal (VAT 0%)', sv: 'Nettobelopp (Moms 0%)' },
  vatFood: { fi: 'Ruoka ALV (14%)', en: 'Food VAT (14%)', sv: 'Matmoms (14%)' },
  vatBeverage: { fi: 'Juoma ALV (25.5%)', en: 'Beverage VAT (25.5%)', sv: 'Dryckesmoms (25.5%)' },
  totalToPay: { fi: 'Yhteensä (sis. ALV)', en: 'Total (incl. VAT)', sv: 'Totalt (inkl. moms)' },
  tableNoteLabel: { fi: 'Pöydän lisätiedot / Toiveet', en: 'Table Notes / Requests', sv: 'Bordsanteckningar' },
  confirmAndOrder: { fi: 'Vahvista & Lähetä Tilaus', en: 'Confirm & Send Order', sv: 'Bekräfta & Skicka Beställning' },
  viewCart: { fi: 'Näytä tilaus', en: 'View Order', sv: 'Visa beställning' },

  // Live Tracking
  orderStatusTitle: { fi: 'Tilauksen Tila', en: 'Order Status', sv: 'Beställningsstatus' },
  orderPlacedAt: { fi: 'Tilausaika', en: 'Ordered at', sv: 'Beställningstid' },
  stepSubmitted: { fi: 'Vastaanotettu', en: 'Submitted', sv: 'Mottagen' },
  stepConfirmed: { fi: 'Vahvistettu', en: 'Confirmed', sv: 'Bekräftad' },
  stepPreparing: { fi: 'Uunissa', en: 'In the Oven', sv: 'I ugnen' },
  stepReady: { fi: 'Valmis', en: 'Ready to Serve', sv: 'Klar' },
  stepCompleted: { fi: 'Tarjoiltu', en: 'Completed', sv: 'Serverad' },
  cancelOrderBeforeConfirm: { fi: 'Peruuta Tilaus', en: 'Cancel Order', sv: 'Avbryt beställning' },
  requestChangeBtn: { fi: 'Pyydä Muutosta Tilaukseen', en: 'Request Order Change', sv: 'Begär ändring av beställning' },
  changePendingKitchen: { fi: 'Muutospyyntö lähetetty keittiölle tarkistettavaksi...', en: 'Change request sent to kitchen for approval...', sv: 'Ändringsbegäran skickad till köket för granskning...' },
  changeApprovedNotice: { fi: 'Keittiö on hyväksynyt pyytämäsi muutoksen!', en: 'Kitchen has approved your change request!', sv: 'Köket har godkänt din ändringsbegäran!' },
  changeRejectedNotice: { fi: 'Keittiö ei voinut hyväksyä muutosta (annos oli jo uunissa).', en: 'Kitchen could not accept the change (already in the oven).', sv: 'Köket kunde inte godkänna ändringen (redan i ugnen).' },

  // Staff / Kitchen Dashboard
  kitchenBoard: { fi: 'Keittiön & Salin Tilaukset', en: 'Kitchen & Dining Orders', sv: 'Köks- & Bordsbeställningar' },
  liveOrdersCount: { fi: 'Aktiivista tilausta', en: 'Active orders', sv: 'Aktiva beställningar' },
  soundOn: { fi: 'Ääni Päällä 🔔', en: 'Sound On 🔔', sv: 'Ljud På 🔔' },
  soundOff: { fi: 'Ääni Pois 🔕', en: 'Sound Off 🔕', sv: 'Ljud Av 🔕' },
  actionConfirm: { fi: 'Vahvista', en: 'Confirm', sv: 'Bekräfta' },
  actionInOven: { fi: 'Uuniin / Valmista', en: 'Start Oven', sv: 'Starta ugn' },
  actionReady: { fi: 'Valmis tarjoiluun', en: 'Ready', sv: 'Klar' },
  actionComplete: { fi: 'Tarjoiltu / Maksettu', en: 'Served / Done', sv: 'Serverad / Klar' },
  actionWhatsApp: { fi: 'Avaa WhatsApp', en: 'Open WhatsApp', sv: 'Öppna WhatsApp' },
  approveChange: { fi: 'Hyväksy muutos', en: 'Approve Change', sv: 'Godkänn ändring' },
  rejectChange: { fi: 'Hylkää muutos', en: 'Reject Change', sv: 'Avvisa ändring' },

  // QR Studio
  qrStudioTitle: { fi: 'Pöytien QR-Koodistudio', en: 'Table QR Code Studio', sv: 'Bords QR-kodstudio' },
  qrStudioSub: { fi: 'Luo ja tulosta laadukkaat pöytästandit ja QR-koodit ravintolasaliin.', en: 'Generate and print high quality table standees and QR codes for your dining area.', sv: 'Skapa och skriv ut bordsstativ och QR-koder för din matsal.' },
  downloadQr: { fi: 'Lataa QR (PNG)', en: 'Download QR (PNG)', sv: 'Ladda ner QR (PNG)' },
  printAllSheets: { fi: 'Tulosta A4 Pöytäkortit', en: 'Print A4 Table Sheets', sv: 'Skriv ut A4 bordskort' },

  // Menu Admin
  menuAdminTitle: { fi: 'Menun Hallinta', en: 'Menu Management', sv: 'Menyhantering' },
  addItemBtn: { fi: 'Lisää Uusi Annos', en: 'Add New Dish', sv: 'Lägg till ny rätt' },
  availabilityToggle: { fi: 'Saatavuus', en: 'In Stock', sv: 'Tillgänglighet' },

  // Analytics
  analyticsTitle: { fi: 'Myynti- & Toimintaraportit', en: 'Sales & Operational Analytics', sv: 'Försäljnings- & Driftrapporter' },
  totalRevenue: { fi: 'Kokonaismyynti', en: 'Total Revenue', sv: 'Total omsättning' },
  totalOrders: { fi: 'Tilauksia Yhteensä', en: 'Total Orders', sv: 'Totalt antal beställningar' },
  avgOrderValue: { fi: 'Keskiostos (AOV)', en: 'Average Order Value', sv: 'Snittorder (AOV)' },
  activeTables: { fi: 'Aktiiviset Pöydät', en: 'Active Tables', sv: 'Aktiva bord' },
  repeatRate: { fi: 'Kanta-asiakkaat', en: 'Repeat Customers', sv: 'Återkommande gäster' }
};

interface LanguageContextType {
  lang: Language;
  setLang: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType>({
  lang: 'en',
  setLang: () => {},
  t: (key: string) => key
});

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [lang, setLang] = useState<Language>(() => {
    const saved = localStorage.getItem('ovenstory_lang');
    return (saved === 'en' || saved === 'sv' || saved === 'fi') ? saved : 'en';
  });

  useEffect(() => {
    localStorage.setItem('ovenstory_lang', lang);
    document.documentElement.lang = lang;
  }, [lang]);

  const t = (key: string): string => {
    if (translations[key] && translations[key][lang]) {
      return translations[key][lang];
    }
    return key;
  };

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);
